import { useCallback, useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Menu, MenuButton, MenuItems } from '@headlessui/react';
import { BellIcon, CheckIcon } from '@heroicons/react/24/outline';
import {
  useNotificationList,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useItems,
  type ItemWithInstanceCounts,
} from '@pantry/api-client';
import { NotificationItem } from './NotificationItem';
import { getNotificationDeepLink } from '@pantry/shared';
import { useNavigate } from '@tanstack/react-router';
import { useSelectedHousehold } from '../../contexts/HouseholdContext';

/**
 * Notification center dropdown with bell icon and badge.
 * Displays paginated notifications with mark as read functionality.
 */
export function NotificationDropdown() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { selectedHousehold } = useSelectedHousehold();
  const { data: unreadData } = useUnreadCount();
  const {
    data: notificationsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useNotificationList({ limit: 10 });

  const markAsReadMutation = useMarkAsRead();
  const markAllAsReadMutation = useMarkAllAsRead();

  const unreadCount = unreadData?.count ?? 0;
  const notifications = notificationsData?.pages.flatMap((page) => page.items) ?? [];

  // Fetch items from the current household to resolve names
  // This leverages React Query's caching - items are likely already loaded from inventory
  const { data: householdItems } = useItems(selectedHousehold?.id || '', undefined);

  // Build a map of itemId -> item for quick lookup
  const itemsMap = useMemo(() => {
    const map = new Map<string, ItemWithInstanceCounts>();
    if (householdItems) {
      for (const item of householdItems) {
        map.set(item.id, item);
      }
    }
    return map;
  }, [householdItems]);

  // Get the markAllCursor from the first page (most recent notification)
  const markAllCursor = notificationsData?.pages[0]?.markAllCursor ?? null;

  const handleMarkAsRead = useCallback(
    (notificationId: string) => {
      markAsReadMutation.mutate(notificationId);
    },
    [markAsReadMutation]
  );

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate(markAllCursor);
  }, [markAllAsReadMutation, markAllCursor]);

  const handleNotificationClick = useCallback(
    (notification: (typeof notifications)[0]) => {
      const deepLink = getNotificationDeepLink(notification);
      if (deepLink) {
        // Navigate to the deep link path
        navigate({ to: deepLink });
      }
    },
    [navigate]
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <Menu as="div" className="relative">
      {/* Bell button with badge */}
      <MenuButton className="relative rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
        <span className="sr-only">
          <Trans>View notifications</Trans>
        </span>
        <BellIcon aria-hidden="true" className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </MenuButton>

      {/* Dropdown panel */}
      <MenuItems
        transition
        className="absolute right-0 z-50 mt-2 w-80 sm:w-96 origin-top-right rounded-lg bg-popover border border-border shadow-lg transition data-closed:scale-95 data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-foreground">
            <Trans>Notifications</Trans>
          </h3>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              disabled={markAllAsReadMutation.isPending}
              className="flex items-center gap-1 text-xs font-medium text-primary hover:text-primary/80 disabled:opacity-50 transition-colors"
            >
              <CheckIcon className="h-3.5 w-3.5" />
              <Trans>Mark all read</Trans>
            </button>
          )}
        </div>

        {/* Notification list */}
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <svg className="animate-spin h-5 w-5 text-muted-foreground" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted mb-3">
                <BellIcon className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground">
                <Trans>No notifications yet</Trans>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                <Trans>We&apos;ll notify you when something important happens</Trans>
              </p>
            </div>
          ) : (
            <div className="py-1">
              {notifications.map((notification) => {
                // Get the item from the map if available
                const data = notification.data as Record<string, unknown> | null;
                const itemId = data?.itemId as string | undefined;
                const item = itemId ? itemsMap.get(itemId) : undefined;

                return (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    item={item}
                    onMarkAsRead={handleMarkAsRead}
                    onClick={() => handleNotificationClick(notification)}
                  />
                );
              })}

              {/* Load more button */}
              {hasNextPage && (
                <div className="px-3 py-2">
                  <button
                    type="button"
                    onClick={handleLoadMore}
                    disabled={isFetchingNextPage}
                    className="flex w-full items-center justify-center rounded-lg py-2 text-xs font-medium text-primary hover:bg-accent disabled:opacity-50 transition-colors"
                  >
                    {isFetchingNextPage ? <Trans>Loading...</Trans> : <Trans>Load more</Trans>}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </MenuItems>
    </Menu>
  );
}
